import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { GoogleMap, useJsApiLoader, DrawingManager, Polygon } from '@react-google-maps/api';
import 'react-toastify/dist/ReactToastify.css';
import { obtenerSectores, guardarSector, eliminarSector } from '../../services/ConfiguracionServicio';
import config from '../../config/appConfig';

// Configuración de Google Maps
const libraries = ['drawing'];
const mapContainerStyle = { width: '100%', height: '400px' };
const center = { lat: -33.4489, lng: -70.6693 }; // Santiago

// --- HELPER DE FORMATO CLP ---
const formatPrecio = (valor) => {
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0
    }).format(valor || 0);
};

// --- COMPONENTE: MAPA DE DIBUJO ---
const MapaZona = ({ coordenadas, onPolygonComplete, isEditable }) => {
    const handlePolygonComplete = (polygon) => {
        const path = polygon.getPath().getArray().map(latLng => ({
            lat: latLng.lat(),
            lng: latLng.lng()
        }));
        onPolygonComplete(path);
        polygon.setMap(null); 
    };

    return (
        <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={12}
            center={coordenadas && coordenadas.length > 0 ? coordenadas[0] : center}
            options={{ streetViewControl: false, mapTypeControl: false }}
        >
            {isEditable && (
                <DrawingManager
                    onPolygonComplete={handlePolygonComplete}
                    options={{
                        drawingControl: true,
                        drawingControlOptions: { drawingModes: ['polygon'] },
                        polygonOptions: {
                            fillColor: '#007bff',
                            fillOpacity: 0.3,
                            strokeWeight: 2,
                            clickable: true,
                            editable: true,
                        },
                    }}
                />
            )}
            {coordenadas && coordenadas.length > 0 && (
                <Polygon
                    paths={coordenadas}
                    options={{
                        fillColor: '#007bff',
                        fillOpacity: 0.4,
                        strokeColor: '#0056b3',
                        strokeWeight: 3,
                    }}
                />
            )}
        </GoogleMap>
    );
};

// --- COMPONENTE: MODAL DE FORMULARIO ---
const SectorFormModal = ({ isVisible, onClose, onSave, sectorToEdit }) => {
    const [sector, setSector] = useState({ 
        nombre_sector: '', valor_entrega: 0, monto_minimo_pedido: 0, 
        tiempo_estimado: '30-45 min', activo: 1, poligono: null 
    });
    const [isLoading, setIsLoading] = useState(false);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: config.google_api, 
        libraries: libraries
    });

    useEffect(() => {
        if (isVisible) {
            setSector(sectorToEdit || { 
                nombre_sector: '', valor_entrega: 0, monto_minimo_pedido: 0, 
                tiempo_estimado: '30-45 min', activo: 1, poligono: null 
            });
        }
    }, [sectorToEdit, isVisible]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!sector.nombre_sector) return toast.warning("Nombre obligatorio");
        if (!sector.poligono) return toast.warning("Debes dibujar la zona en el mapa");

        setIsLoading(true);
        try {
            await guardarSector(sector);
            onSave();
        } catch (err) { toast.error("Error al guardar"); }
        finally { setIsLoading(false); }
    };

    if (!isVisible) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', zIndex: 1050 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-xl">
                    <div className="modal-header border-0 pt-4 px-4">
                        <h5 className="font-weight-bold m-0">{sectorToEdit ? 'Editar Zona de Reparto' : 'Nueva Zona de Reparto'}</h5>
                        <button className="close" onClick={onClose}>&times;</button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body p-4">
                            <div className="row">
                                <div className="col-md-5">
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Nombre del Sector</label>
                                        <input type="text" className="form-control border-light bg-light" value={sector.nombre_sector} onChange={(e) => setSector({ ...sector, nombre_sector: e.target.value })} placeholder="Ej: Providencia Norte" />
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Costo de Envío</label>
                                        <div className="input-group">
                                            <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                                            <input type="number" className="form-control border-light bg-light" value={sector.valor_entrega} onChange={(e) => setSector({ ...sector, valor_entrega: e.target.value })} />
                                        </div>
                                        <small className="text-primary font-weight-bold">{formatPrecio(sector.valor_entrega)}</small>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Mínimo de Compra</label>
                                        <div className="input-group">
                                            <div className="input-group-prepend"><span className="input-group-text">$</span></div>
                                            <input type="number" className="form-control border-light bg-light" value={sector.monto_minimo_pedido} onChange={(e) => setSector({ ...sector, monto_minimo_pedido: e.target.value })} />
                                        </div>
                                        <small className="text-muted">{formatPrecio(sector.monto_minimo_pedido)}</small>
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block">Tiempo de Entrega</label>
                                        <input type="text" className="form-control border-light bg-light" value={sector.tiempo_estimado} onChange={(e) => setSector({ ...sector, tiempo_estimado: e.target.value })} />
                                    </div>
                                </div>
                                <div className="col-md-7">
                                    <label className="text-uppercase small font-weight-bold text-muted mb-2 d-block text-center">Dibuja la zona en el mapa</label>
                                    <div className="rounded border overflow-hidden shadow-sm">
                                        {isLoaded ? (
                                            <MapaZona coordenadas={sector.poligono} onPolygonComplete={(path) => setSector({ ...sector, poligono: path })} isEditable={true} />
                                        ) : <div className="p-5 text-center">Cargando Mapa...</div>}
                                    </div>
                                    <p className="small text-muted mt-2 text-center">Haz clic en el mapa para empezar a marcar puntos.</p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer border-0 p-4 pt-0">
                            <button type="button" className="btn btn-link text-muted" onClick={onClose}>Cancelar</button>
                            <button type="submit" className="btn btn-primary btn-lg rounded-pill px-5 shadow" disabled={isLoading}>{isLoading ? 'Guardando...' : 'Guardar Zona'}</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL ---
const AdministradorDespachos = ({ embedded }) => {
    const [sectores, setSectores] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sectorToEdit, setSectorToEdit] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => { cargarSectores(); }, []);

    const cargarSectores = async () => {
        setIsLoading(true);
        try {
            const data = await obtenerSectores();
            const procesados = data.map(s => {
                let poli = s.poligono;
                if (typeof poli === 'string' && poli !== "") {
                    try { poli = JSON.parse(poli); } catch (e) { poli = null; }
                }
                return { ...s, poligono: poli };
            });
            setSectores(procesados || []);
        } catch (err) { toast.error("Error de conexión"); }
        finally { setIsLoading(false); }
    };

    const handleDelete = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar la zona "${nombre}"?`)) return;
        try {
            await eliminarSector(id);
            toast.success("Zona eliminada");
            cargarSectores();
        } catch (err) { toast.error("No se pudo eliminar"); }
    };

    const handleSave = () => {
        setIsModalOpen(false);
        setSectorToEdit(null);
        toast.success("¡Datos actualizados!");
        cargarSectores();
    };

    const filtered = sectores.filter(s => s.nombre_sector.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="bg-light min-vh-100 py-5 px-3">
            <ToastContainer position="bottom-center" autoClose={2500} hideProgressBar />
            <div className="container" style={{ maxWidth: '1100px' }}>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-5">
                    <div>
                        <h1 className="h2 font-weight-bold text-dark mb-1">Zonas de Despacho (Polígonos)</h1>
                        <p className="text-muted">Define los perímetros de entrega y sus costos en CLP.</p>
                    </div>
                    <button className="btn btn-primary btn-lg rounded-pill shadow-lg px-4" onClick={() => { setSectorToEdit(null); setIsModalOpen(true); }}>
                        <i className="fas fa-draw-polygon mr-2"></i> Nueva Zona
                    </button>
                </div>

                <div className="card border-0 shadow-sm rounded-xl overflow-hidden">
                    <div className="card-header bg-white border-0 pt-4 px-4 pb-2">
                        <div className="input-group bg-light rounded-pill px-3 border shadow-inner">
                            <div className="input-group-prepend border-0 bg-transparent">
                                <span className="input-group-text bg-transparent border-0 text-muted"><i className="fas fa-search"></i></span>
                            </div>
                            <input type="text" className="form-control bg-transparent border-0" placeholder="Buscar por nombre de sector..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="bg-light text-muted small text-uppercase">
                                    <tr>
                                        <th className="border-0 px-4 py-3">Sector</th>
                                        <th className="border-0 py-3">Costo Envío</th>
                                        <th className="border-0 py-3">Mínimo Pedido</th>
                                        <th className="border-0 py-3 text-center">Estado</th>
                                        <th className="border-0 px-4 py-3 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                                    ) : filtered.map(s => (
                                        <tr key={s.id}>
                                            <td className="px-4 py-3">
                                                <div className="font-weight-bold text-dark">{s.nombre_sector}</div>
                                                <small className="text-muted"><i className="far fa-clock mr-1"></i> {s.tiempo_estimado}</small>
                                            </td>
                                            <td className="py-3 font-weight-bold text-primary">{formatPrecio(s.valor_entrega)}</td>
                                            <td className="py-3 text-dark">{formatPrecio(s.monto_minimo_pedido)}</td>
                                            <td className="py-3 text-center">
                                                <span className={`badge px-3 py-2 rounded-pill ${s.activo ? 'badge-soft-success' : 'badge-soft-secondary'}`}>
                                                    {s.activo ? 'Habilitado' : 'Deshabilitado'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button className="btn btn-icon btn-light rounded-circle mr-2 text-primary" onClick={() => { setSectorToEdit(s); setIsModalOpen(true); }} title="Editar polígono y datos"><i className="fas fa-pen"></i></button>
                                                <button className="btn btn-icon btn-light rounded-circle text-danger" onClick={() => handleDelete(s.id, s.nombre_sector)} title="Eliminar zona"><i className="fas fa-trash"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!isLoading && filtered.length === 0 && <div className="text-center py-5 text-muted">No hay zonas configuradas.</div>}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .rounded-xl { border-radius: 1.25rem !important; }
                .badge-soft-success { background-color: #e6fcf5; color: #087f5b; }
                .badge-soft-secondary { background-color: #f1f3f5; color: #495057; }
                .btn-icon { width: 36px; height: 36px; border: none; display: inline-flex; align-items: center; justify-content: center; transition: 0.2s; }
                .btn-icon:hover { background-color: #e9ecef; transform: scale(1.1); }
                .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); }
            `}</style>

            <SectorFormModal isVisible={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} sectorToEdit={sectorToEdit} />
        </div>
    );
};

export default AdministradorDespachos;